import { useTelesalesCustomerNumber } from "@tm/context-distribution"
import { Paging } from "@tm/models"
import { notUndefinedOrNull } from "@tm/utils"
import { useMemo } from "react"
import { QueryFunctionContext, QueryKey, useInfiniteQuery } from "react-query"
import { ShowBackordersRequest } from "./models"
import { showBackorders } from "./showBackorders"
import { CisFunctionType, useShowCisOptions } from "../../options"

type PageParam = Paging & { continuationPoint?: string }

function getBackorders(pageParam: PageParam | undefined, pageSize: number, customerNo: string | undefined) {
    const showBackordersRequest: ShowBackordersRequest = {
        pageIndex: pageParam?.pageIndex || 1,
        pageSize: pageParam?.pageSize || pageSize,
        continuationPoint: pageParam?.continuationPoint,
        customerNo,
    }

    return showBackorders(showBackordersRequest)
}

export const CIS_BACKORDERS_KEY = "cis_useBackorders"
export function useBackorders(pageSize = 150) {
    const { telesalesCustomerNo, enableServiceCalls } = useTelesalesCustomerNumber()
    const { cisOptions } = useShowCisOptions()

    const enabled = useMemo(() => {
        return enableServiceCalls && !!cisOptions?.cisFunctions?.some((cisFunction) => cisFunction.functionType === CisFunctionType.Backorders)
    }, [cisOptions?.cisFunctions, enableServiceCalls])

    const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery(
        [CIS_BACKORDERS_KEY, telesalesCustomerNo, pageSize, enabled],
        ({ pageParam }: QueryFunctionContext<QueryKey, PageParam>) => {
            return getBackorders(pageParam, pageSize, telesalesCustomerNo)
        },
        {
            enabled,
            getNextPageParam: (lastPage, allPages): PageParam | undefined => {
                if (
                    lastPage?.useContinuationPoint &&
                    lastPage?.continuationPoint &&
                    lastPage.continuationPoint !== allPages[allPages.length - 2]?.continuationPoint
                ) {
                    return {
                        pageIndex: lastPage.pageIndex + 1,
                        pageSize: lastPage.pageSize,
                        continuationPoint: lastPage.continuationPoint,
                    }
                }
                if (lastPage && !lastPage?.useContinuationPoint) {
                    return {
                        pageIndex: lastPage.pageIndex + 1,
                        pageSize: lastPage.pageSize,
                    }
                }
            },
        }
    )

    const backorders = useMemo(() => {
        return data?.pages.flatMap((page) => page?.backorders).filter(notUndefinedOrNull)
    }, [data?.pages])

    return {
        backorders,
        backordersLoading: isLoading,
        backordersLoadNextPage: hasNextPage ? fetchNextPage : undefined,
        backordersNextPageLoading: isFetchingNextPage,
    }
}
